import PropTypes from 'prop-types';

import { cn } from '#/lib/utils';
import { formatDateOrDash } from '#/utils/date';
import { formatDisplayAmount } from '#/utils/formatNumber';

function amountOrDash(value) {
  return value === null || value === undefined || value === ''
    ? '—'
    : formatDisplayAmount(value);
}

function percentOrDash(value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  const n = Number(value);
  return Number.isFinite(n) ? `${Math.round(n * 10000) / 10000}%` : '—';
}

const CELL = 'px-3 py-2 text-sm tabular-nums';

/**
 * 付款期別唯讀表：目前有效計畫（含已付／剩餘）或異動擬議期別（含已付／待退款）。
 * `paymentDataUnknown`：歷史實付未登錄（HISTORICAL_UNKNOWN），已付欄顯示「付款資料未登錄」。
 */
const PurchasePaymentPhasesTable = ({
  phases = [],
  variant = 'current',
  paymentDataUnknown = false,
}) => {
  if (phases.length === 0) {
    return <p className="text-sm text-muted-foreground">尚無付款期別</p>;
  }

  const showRefund = variant === 'projection';

  return (
    <div className="overflow-x-auto rounded-md border border-gray-300">
      <table className="w-full min-w-[36rem] border-collapse">
        <thead className="bg-gray-100 text-xs font-medium text-gray-600">
          <tr>
            <th className={cn(CELL, 'text-left')}>期別</th>
            <th className={cn(CELL, 'text-right')}>金額</th>
            <th className={cn(CELL, 'text-right')}>比例</th>
            <th className={cn(CELL, 'text-left')}>到期日</th>
            <th className={cn(CELL, 'text-right')}>已付</th>
            <th className={cn(CELL, 'text-right')}>{showRefund ? '待退款' : '剩餘'}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {phases.map((phase, index) => {
            const tail = showRefund ? phase?.pendingRefundAmount : phase?.remainingAmount;
            return (
              <tr key={phase?.id ?? phase?.phaseId ?? `phase-${index}`}>
                <td className={cn(CELL, 'text-left')}>{`第 ${phase?.phaseNo ?? index + 1} 期`}</td>
                <td className={cn(CELL, 'text-right')}>{amountOrDash(phase?.amount)}</td>
                <td className={cn(CELL, 'text-right')}>{percentOrDash(phase?.percentage)}</td>
                <td className={cn(CELL, 'text-left')}>{formatDateOrDash(phase?.dueDate)}</td>
                <td className={cn(CELL, 'text-right')}>
                  {paymentDataUnknown ? '付款資料未登錄' : amountOrDash(phase?.netPaidAmount)}
                </td>
                <td
                  className={cn(
                    CELL,
                    'text-right',
                    showRefund && Number(tail) > 0 && 'font-medium text-rose-600',
                  )}
                >
                  {paymentDataUnknown ? '—' : amountOrDash(tail)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

PurchasePaymentPhasesTable.propTypes = {
  phases: PropTypes.arrayOf(PropTypes.object),
  variant: PropTypes.oneOf(['current', 'projection']),
  paymentDataUnknown: PropTypes.bool,
};

export default PurchasePaymentPhasesTable;
